/**
 * Demo Mode
 *
 * Controls whether the app uses seed data (demo mode) or a real API. Seeded
 * from VITE_DEMO_MODE at build time; can be toggled at runtime and persisted.
 * Toggling clears all TanStack Query caches so every query re-runs.
 */

import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { env } from "./env";

const STORAGE_KEY = "stocksync-demo-mode";

interface DemoModeState {
  demoMode: boolean;
  setDemoMode: (enabled: boolean) => void;
  toggleDemoMode: () => void;
}

export const useDemoModeStore = create<DemoModeState>()(
  devtools(
    persist(
      subscribeWithSelector((set, get) => ({
        demoMode: env.VITE_DEMO_MODE,
        setDemoMode: (enabled) => set({ demoMode: enabled }),
        toggleDemoMode: () => set({ demoMode: !get().demoMode }),
      })),
      { name: STORAGE_KEY },
    ),
    { name: "demo-mode" },
  ),
);

export const useDemoMode = useDemoModeStore;

export function subscribeToQueryCacheClear(clearAll: () => void): () => void {
  return useDemoModeStore.subscribe(
    (state) => state.demoMode,
    () => clearAll(),
  );
}

/** Synchronous read outside React (interceptors, main.tsx). */
export function isDemoMode(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: { demoMode?: boolean } };
      if (typeof parsed?.state?.demoMode === "boolean") return parsed.state.demoMode;
    }
  } catch {
    // ignore
  }
  return env.VITE_DEMO_MODE;
}
